import { AxiosError } from 'axios';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
// import crashlytics from "@react-native-firebase/crashlytics";
import i18n, { applyTranslations } from '@app/i18n';
import { IErrorData } from '@app/interfaces/apiError';
import { ICanton } from '@app/interfaces/config';
import { cantonWhitelist, configVersion } from '@app/constants/whitelist';
import { RootState, Store } from '../store';
import { seasonEndOffsetInDaysDefault } from '@app/constants/waterbody';
import {
  defaultFormatLongDE,
  defaultFormatShortDE,
  defaultFormatLongEN,
  defaultFormatLongFR,
  defaultFormatLongIT,
  defaultFormatShortEN,
  defaultFormatShortFR,
  defaultFormatShortIT,
} from '@app/constants/dateFormats';
import { getConfigRequest } from '@app/api/config';

export interface IState {
  cantons: ICanton[];
  currentCanton?: ICanton;
  initialConfigFetched: boolean;
}

export interface IFulfilledConfigPayload {
  currentCanton?: ICanton;
  cantons: ICanton[];
}

export const languagesListDefault = ['en', 'de', 'fr', 'it'];

export const cantonsInit = [
  {
    accountIconUrl: 'https://i.picsum.photos/id/124/50/50.jpg?hmac=_2zGwn9CyAOBnGyCPlV2SZ-c64jgrtlXXVrs78KBu6Q',
    accountId: 1,
    apiBaseUrl: 'https://gr-api.fishven.com',
    controlAppQRCodeActivated: false,
    languageDefault: 'de',
    languagesList: languagesListDefault,
    name: 'Graubünden',
    privacyPolicyVersion: 1,
    qrCodeLicenseActivated: true,
    seasonalQuota: 60,
    sessionEndHour: 23,
    sessionStartHour: 5,
    statisticsTimeSharesRanges: [5, 10, 14, 18, 23],
    supportEmail: 'support@fishven.com',
    webShopLink: 'https://intshop-ajf.gr.ch/de/',
  },
];

const initialState = {
  cantons: cantonsInit,
  currentCanton: undefined,
  initialConfigFetched: false,
} as IState;

const slice = createSlice({
  name: 'config',
  initialState,
  reducers: {
    setCanton(state, { payload }: { payload: string }) {
      const cantonName = payload;
      const targetCanton = state.cantons.find((cn: ICanton) => cn.name === cantonName);

      if (targetCanton && targetCanton.name === cantonName) {
        return {
          ...state,
          currentCanton: targetCanton,
        };
      }

      return state;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAppConfig.fulfilled, (state: IState, { payload }) => {
      console.log(
        '[Config] Whitelist filter - checking cantons:',
        (payload as IFulfilledConfigPayload).cantons.map((c) => ({ name: c.name, apiBaseUrl: c.apiBaseUrl })),
      );
      console.log(
        '[Config] Whitelist domains:',
        cantonWhitelist.map((cwl) => cwl.apiDomain),
      );

      const payloadCantons = (payload as IFulfilledConfigPayload).cantons.filter((pc) => {
        const matches = cantonWhitelist.some((cwl) => {
          const includes = pc.apiBaseUrl.includes(cwl.apiDomain);
          if (includes) {
            console.log(`[Config] Canton "${pc.name}" (${pc.apiBaseUrl}) matches whitelist domain: ${cwl.apiDomain}`);
          }
          return includes;
        });
        if (!matches) {
          console.warn(`[Config] Canton "${pc.name}" (${pc.apiBaseUrl}) does NOT match any whitelist domain`);
        }
        return matches;
      });

      console.log(
        '[Config] Filtered cantons (passed whitelist):',
        payloadCantons.map((c) => ({ name: c.name, apiBaseUrl: c.apiBaseUrl })),
      );
      const matchedCurrentCanton = payloadCantons.find(
        (payloadCanton) => payloadCanton.accountId === state.currentCanton?.accountId,
      );

      const payloadActiveCanton = matchedCurrentCanton?.accountId
        ? {
            ...state.currentCanton,
            ...matchedCurrentCanton,
          }
        : payloadCantons.length === 1
          ? payloadCantons[0]
          : undefined;

      if (payloadActiveCanton?.translations) {
        console.log('[Config] Applying translations for canton:', payloadActiveCanton.name);
        console.log('[Config] Translations structure:', payloadActiveCanton.translations);
        console.log('[Config] Has fishven-admin-tool?', !!payloadActiveCanton.translations['fishven-admin-tool']);
        applyTranslations(payloadActiveCanton?.translations, payloadActiveCanton?.languageDefault);
      } else {
        console.warn('[Config] No translations found for active canton:', payloadActiveCanton?.name);
      }

      if (payloadActiveCanton?.languageDefault) {
        i18n.changeLanguage(payloadActiveCanton.languageDefault);
      }

      const cantons = payloadCantons.map((payloadCanton) => {
        const localValue = state.cantons.find((lc) => lc.accountId === payloadCanton.accountId);

        if (localValue?.accountId) {
          return {
            ...localValue,
            ...payloadCanton,
            agreedWithVersion: localValue.agreedWithVersion,
          };
        } else {
          return {
            ...payloadCanton,
          };
        }
      });

      return {
        ...state,
        cantons,
        currentCanton: payloadActiveCanton,
        initialConfigFetched: true,
      };
    });
  },
});

export const getAppConfig = createAsyncThunk('getAppConfig', async (_, { rejectWithValue }) => {
  try {
    try {
      const { data } = await getConfigRequest(configVersion);

      console.log('=== Config API Response ===');
      console.log('Raw API response data:', data);
      console.log('Config version:', configVersion);

      const cantons = (data as Array<{ configuration: ICanton }>).map((remoteItem) => {
        console.log('Processing canton config:', remoteItem.configuration.name);
        console.log('showCatchFishingType from API:', remoteItem.configuration.showCatchFishingType);
        console.log('catchFishingTypeMandatory from API:', remoteItem.configuration.catchFishingTypeMandatory);

        const mergedDateFormats = {
          de: {
            long: remoteItem.configuration?.dateFormats?.de?.long
              ? remoteItem.configuration.dateFormats.de.long
              : defaultFormatLongDE,
            short: remoteItem.configuration?.dateFormats?.de?.short
              ? remoteItem.configuration.dateFormats.de.short
              : defaultFormatShortDE,
          },
          en: {
            long: remoteItem.configuration?.dateFormats?.en?.long
              ? remoteItem.configuration.dateFormats.en.long
              : defaultFormatLongEN,
            short: remoteItem.configuration?.dateFormats?.en?.short
              ? remoteItem.configuration.dateFormats.en.short
              : defaultFormatShortEN,
          },
          // Italian: always use app defaults (see defaultFormatLongIT in constants/dateFormats.ts).
          // The API often sends FR-style slashes (DD/MM/YYYY) for `it`; that must not override.
          it: {
            long: defaultFormatLongIT,
            short: defaultFormatShortIT,
          },
          fr: {
            long: remoteItem.configuration?.dateFormats?.fr?.long
              ? remoteItem.configuration.dateFormats.fr.long
              : defaultFormatLongFR,
            short: remoteItem.configuration?.dateFormats?.fr?.short
              ? remoteItem.configuration.dateFormats.fr.short
              : defaultFormatShortFR,
          },
        };

        return {
          ...remoteItem.configuration,
          length_input_mm:
            remoteItem.configuration.length_input_mm !== undefined ? remoteItem.configuration.length_input_mm : true,
          sanaMaskVersion:
            remoteItem.configuration.sanaMaskVersion != null ? remoteItem.configuration.sanaMaskVersion : 1,
          seasonEndOffsetInDays: remoteItem.configuration.seasonEndOffsetInDays
            ? remoteItem.configuration.seasonEndOffsetInDays
            : seasonEndOffsetInDaysDefault,
          defaultLatitude: remoteItem.configuration.defaultLatitude || '46.8491852',
          defaultLongitude: remoteItem.configuration.defaultLongitude || '9.5141989',
          dateFormats: mergedDateFormats,
        };
      }) as Array<ICanton>;

      console.log('Processed cantons array:', cantons);
      console.log(
        'Cantons with showCatchFishingType:',
        cantons.map((c) => ({
          name: c.name,
          showCatchFishingType: c.showCatchFishingType,
          catchFishingTypeMandatory: c.catchFishingTypeMandatory,
        })),
      );
      console.log('===========================');

      const store = Store.getState() as RootState;
      const currentCanton = store.config.currentCanton;

      console.log('Current canton after processing:', currentCanton);
      console.log('Current canton showCatchFishingType:', currentCanton?.showCatchFishingType);
      console.log('Current canton catchFishingTypeMandatory:', currentCanton?.catchFishingTypeMandatory);

      return { cantons, currentCanton };
    } catch (error) {
      //   crashlytics().log('Error while trying config. (inner try catch)');
      //   if (error.isAxiosError) {
      //     crashlytics().log(`Config error code: ${error?.code}. Status code is: ${error?.response?.status}`);
      //   }
      //   crashlytics().recordError(error as Error);
      console.log(error);
    }
  } catch (error) {
    const err = error as AxiosError;
    // crashlytics().log('Error while trying config.');
    // crashlytics().recordError(error as Error);
    return rejectWithValue(err.response?.data as IErrorData);
  }
});

export const { setCanton } = slice.actions;

export const config = slice.reducer;
